<script setup>
	import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

	const props = defineProps({
		items: {
			type: Array,
			required: true,
		},
		intervalMilliseconds: {
			type: Number,
			default: 7000,
		},
		transitionMilliseconds: {
			type: Number,
			default: 850,
		},
	});

	const itemsPerPage = ref(3);
	const currentPageIndex = ref(0);
	const isTransitionEnabled = ref(true);
	const isPaused = ref(false);
	const isMotionReduced = ref(false);

	let advanceTimer = null;

	function detectItemsPerPage() {
		if (typeof window === "undefined") {
			return 3;
		}

		if (window.matchMedia("(max-width: 640px)").matches) {
			return 1;
		}

		if (window.matchMedia("(max-width: 992px)").matches) {
			return 2;
		}

		return 3;
	}

	const pages = computed(() => {
		const grouped = [];

		for (let startIndex = 0; startIndex < props.items.length; startIndex += itemsPerPage.value) {
			grouped.push(props.items.slice(startIndex, startIndex + itemsPerPage.value));
		}

		return grouped;
	});

	const hasMultiplePages = computed(() => pages.value.length > 1);

	const displayPages = computed(() => {
		if (!hasMultiplePages.value) {
			return pages.value;
		}

		return [...pages.value, pages.value[0]];
	});

	const activeDotIndex = computed(() => {
		if (pages.value.length === 0) {
			return 0;
		}

		return currentPageIndex.value % pages.value.length;
	});

	const trackOffsetPercent = computed(() => currentPageIndex.value * 100);

	const effectiveTransitionMilliseconds = computed(() => {
		return isMotionReduced.value ? 0 : props.transitionMilliseconds;
	});

	function enableTransitionAfterFrame() {
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				isTransitionEnabled.value = true;
			});
		});
	}

	function jumpWithoutAnimation(targetIndex) {
		isTransitionEnabled.value = false;
		currentPageIndex.value = targetIndex;
		enableTransitionAfterFrame();
	}

	function goToNextPage() {
		if (!hasMultiplePages.value) {
			return;
		}

		currentPageIndex.value += 1;
	}

	function goToPreviousPage() {
		if (!hasMultiplePages.value) {
			return;
		}

		if (currentPageIndex.value <= 0) {
			jumpWithoutAnimation(pages.value.length - 1);
			return;
		}

		currentPageIndex.value -= 1;
	}

	function goToPage(pageIndex) {
		currentPageIndex.value = pageIndex;
	}

	function handleTransitionEnd() {
		if (currentPageIndex.value >= pages.value.length) {
			jumpWithoutAnimation(0);
		}
	}

	function pauseAutoplay() {
		isPaused.value = true;
	}

	function resumeAutoplay() {
		isPaused.value = false;
	}

	function startAutoplay() {
		stopAutoplay();

		if (!hasMultiplePages.value || isMotionReduced.value) {
			return;
		}

		advanceTimer = setInterval(() => {
			if (!isPaused.value) {
				goToNextPage();
			}
		}, props.intervalMilliseconds);
	}

	function stopAutoplay() {
		if (advanceTimer) {
			clearInterval(advanceTimer);
			advanceTimer = null;
		}
	}

	function handleResize() {
		const nextItemsPerPage = detectItemsPerPage();

		if (nextItemsPerPage !== itemsPerPage.value) {
			itemsPerPage.value = nextItemsPerPage;
			currentPageIndex.value = 0;
		}
	}

	onMounted(() => {
		isMotionReduced.value = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
		itemsPerPage.value = detectItemsPerPage();
		window.addEventListener("resize", handleResize);
		startAutoplay();
	});

	onBeforeUnmount(() => {
		stopAutoplay();
		window.removeEventListener("resize", handleResize);
	});

	watch(itemsPerPage, () => {
		startAutoplay();
	});
</script>

<template>
	<div
		class="carousel"
		@mouseenter="pauseAutoplay"
		@mouseleave="resumeAutoplay"
		@focusin="pauseAutoplay"
		@focusout="resumeAutoplay"
	>
		<div class="carousel-viewport">
			<div
				class="carousel-track"
				:class="{ 'carousel-track-animated': isTransitionEnabled }"
				:style="{
					transform: `translateX(-${trackOffsetPercent}%)`,
					transitionDuration: `${effectiveTransitionMilliseconds}ms`,
				}"
				@transitionend="handleTransitionEnd"
			>
				<div
					v-for="(page, pageIndex) in displayPages"
					:key="pageIndex"
					class="carousel-page"
				>
					<div
						class="carousel-grid"
						:style="{ gridTemplateColumns: `repeat(${itemsPerPage}, minmax(0, 1fr))` }"
					>
						<template v-for="(item, itemIndex) in page" :key="`${pageIndex}-${itemIndex}`">
							<slot :item="item" :item-index="itemIndex"></slot>
						</template>
					</div>
				</div>
			</div>
		</div>

		<div v-if="hasMultiplePages" class="carousel-controls">
			<button
				type="button"
				class="carousel-arrow"
				aria-label="Previous testimonials"
				@click="goToPreviousPage"
			>
				<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
					<path d="M15 6l-6 6 6 6" />
				</svg>
			</button>

			<div class="carousel-dots" role="tablist">
				<button
					v-for="(page, pageIndex) in pages"
					:key="pageIndex"
					type="button"
					class="carousel-dot"
					:class="{ 'carousel-dot-active': pageIndex === activeDotIndex }"
					:aria-label="`Show testimonials group ${pageIndex + 1}`"
					:aria-selected="pageIndex === activeDotIndex"
					role="tab"
					@click="goToPage(pageIndex)"
				></button>
			</div>

			<button
				type="button"
				class="carousel-arrow"
				aria-label="Next testimonials"
				@click="goToNextPage"
			>
				<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
					<path d="M9 6l6 6-6 6" />
				</svg>
			</button>
		</div>
	</div>
</template>

<style scoped>
	.carousel {
		position: relative;
	}

	.carousel-viewport {
		overflow: hidden;
	}

	.carousel-track {
		display: flex;
		will-change: transform;
	}

	.carousel-track-animated {
		transition-property: transform;
		transition-timing-function: cubic-bezier(0.22, 1, 0.36, 1);
	}

	.carousel-page {
		flex: 0 0 100%;
		min-width: 100%;
		box-sizing: border-box;
	}

	.carousel-grid {
		display: grid;
		gap: var(--carousel-gap, 1.5rem);
		padding: 1rem 0.5rem;
		align-items: stretch;
	}

	.carousel-controls {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 1.25rem;
		margin-top: 2rem;
	}

	.carousel-arrow {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 46px;
		height: 46px;
		flex-shrink: 0;
		border-radius: 50%;
		border: 1px solid var(--carousel-arrow-border, rgba(37, 99, 235, 0.3));
		background: var(--carousel-arrow-bg, #ffffff);
		color: var(--carousel-accent, #2563eb);
		cursor: pointer;
		transition: transform 0.2s ease, background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
	}

	.carousel-arrow svg {
		width: 20px;
		height: 20px;
	}

	.carousel-arrow:hover {
		transform: translateY(-2px);
		background: var(--carousel-accent, #2563eb);
		color: var(--carousel-arrow-hover-text, #ffffff);
		box-shadow: 0 12px 24px -12px var(--carousel-accent, #2563eb);
	}

	.carousel-arrow:focus-visible {
		outline: 3px solid var(--carousel-accent, #2563eb);
		outline-offset: 2px;
	}

	.carousel-dots {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		justify-content: center;
		gap: 0.6rem;
		min-width: 0;
	}

	.carousel-dot {
		width: 10px;
		height: 10px;
		padding: 0;
		border: none;
		border-radius: 999px;
		background: var(--carousel-dot, #cbd5e1);
		cursor: pointer;
		transition: width 0.25s ease, background 0.25s ease;
	}

	.carousel-dot-active {
		width: 30px;
		background: var(--carousel-accent, #2563eb);
	}

	.carousel-dot:focus-visible {
		outline: 3px solid var(--carousel-accent, #2563eb);
		outline-offset: 2px;
	}

	@media (max-width: 640px) {
		.carousel-controls {
			gap: 0.6rem;
			margin-top: 1.5rem;
		}

		.carousel-arrow {
			width: 40px;
			height: 40px;
		}

		.carousel-arrow svg {
			width: 18px;
			height: 18px;
		}

		.carousel-dots {
			flex-wrap: wrap;
			gap: 0.45rem;
			max-width: 56vw;
		}

		.carousel-dot {
			width: 8px;
			height: 8px;
		}

		.carousel-dot-active {
			width: 22px;
		}
	}
</style>